import CustomWidgetModifierOperators from 'ContentUtils/constants/CustomWidgetModifierOperators';
import { BrightnessOffsetModifierTypename, ShadeModifierTypename, ShadeOffsetModifierTypename } from '../../__generated__/chirp/com/hubspot/global/styles/rpc/GlobalStylesRpc';
// Maps CHIRP __typename to FE modifier_type for all supported modifier kinds
const CHIRP_TYPENAME_TO_MOD_TYPE = {
  [ShadeModifierTypename]: CustomWidgetModifierOperators.SHADE,
  [BrightnessOffsetModifierTypename]: CustomWidgetModifierOperators.BRIGHTNESS_OFFSET,
  [ShadeOffsetModifierTypename]: CustomWidgetModifierOperators.SHADE_OFFSET
};

// Inverse: FE modifier_type to CHIRP __typename
const MOD_TYPE_TO_CHIRP_TYPENAME = Object.fromEntries(Object.entries(CHIRP_TYPENAME_TO_MOD_TYPE).map(([k, v]) => [v, k]));

/** Leaf FE→CHIRP: maps a `modifier_type` string enum to a `__typename`-tagged CHIRP modifier union member. */
const fieldModifierToChirpModifier = modifier => {
  const typename = MOD_TYPE_TO_CHIRP_TYPENAME[modifier.modifier_type];
  if (!typename) return undefined;
  return {
    __typename: typename,
    modifierValue: modifier.modifier_type === CustomWidgetModifierOperators.BRIGHTNESS_OFFSET ? Number(modifier.modifier_value) : modifier.modifier_value
  };
};

/** Normalizes a modifier from either CHIRP (__typename/modifierValue) or FE (modifier_type/modifier_value) format into the FE shape. */
const toFieldModifier = modifier => {
  var _ref, _modifier$modifier_va;
  // Try FE key first, fall back to looking up the CHIRP __typename discriminator
  const modType = (_ref = modifier.modifier_type) !== null && _ref !== void 0 ? _ref : CHIRP_TYPENAME_TO_MOD_TYPE[modifier.__typename];
  // FE uses modifier_value (snake_case), CHIRP uses modifierValue (camelCase)
  const modValue = (_modifier$modifier_va = modifier.modifier_value) !== null && _modifier$modifier_va !== void 0 ? _modifier$modifier_va : modifier.modifierValue;
  if (!modType || modValue == null) return undefined;
  return {
    modifier_type: modType,
    modifier_value: modValue
  };
};

/** Single value FE→CHIRP: wraps `property_value_paths` + modifier into CHIRP's nested `bindingConfig` envelope. Empty input → empty `{}` (unbound). */
const inheritedValueToFieldConfig = value => {
  const {
    property_value_paths,
    modifier
  } = value;
  if (!property_value_paths) {
    return {};
  }
  const bindingConfig = {
    propertyValuePaths: property_value_paths
  };
  const chirpModifier = modifier && fieldModifierToChirpModifier(modifier);
  if (chirpModifier) {
    bindingConfig.modifier = chirpModifier;
  }
  return {
    bindingConfig
  };
};

/** Single value CHIRP→FE: unwraps CHIRP's `bindingConfig` back to the flat FE `InheritedValue` shape. Empty input → empty `{}` (unbound). */
const fieldConfigToInheritedValue = config => {
  const {
    bindingConfig
  } = config;
  if (!bindingConfig) return {};
  const result = {
    property_value_paths: bindingConfig.propertyValuePaths
  };
  const fieldModifier = bindingConfig.modifier && toFieldModifier(bindingConfig.modifier);
  if (fieldModifier) {
    result.modifier = fieldModifier;
  }
  return result;
};

/** Map-level CHIRP→FE: converts a record of CHIRP `FieldConfig` into a record of FE `InheritedValue`, keyed by field path. */
export const fieldConfigsToBindings = configs => {
  const bindings = {};
  for (const [fieldPath, config] of Object.entries(configs)) {
    bindings[fieldPath] = fieldConfigToInheritedValue(config);
  }
  return bindings;
};

/** Map-level FE→CHIRP: converts a record of FE `InheritedValue` into a record of CHIRP `FieldConfig`, keyed by field path. */
export const bindingsToFieldConfigs = bindings => {
  const configs = {};
  for (const [fieldPath, binding] of Object.entries(bindings)) {
    configs[fieldPath] = inheritedValueToFieldConfig(binding);
  }
  return configs;
};

/**
 * Hybrid wire shape for non-CHIRP REST render endpoints (e.g. content-editor-rendering's
 * `module-with-meta`). The outer `bindingConfig.propertyValuePaths` stays in CHIRP-style
 * camelCase, but the inner `modifier` switches to Jackson's snake_case discriminator
 * (`modifier_type` / `modifier_value`) — that's what the BE deserializer expects when
 * mapping the request body into `CustomWidget.fieldConfigs`. Use this when posting
 * fieldConfigs to a non-CHIRP route where the chirp-gateway codec doesn't run.
 */

/** Single value CHIRP→Render: converts a CHIRP `FieldConfig` into the REST wire shape. */
const fieldConfigToRenderShape = config => {
  const {
    bindingConfig
  } = config;
  if (!bindingConfig) return {};
  const fieldModifier = bindingConfig.modifier && toFieldModifier(bindingConfig.modifier);
  return {
    bindingConfig: Object.assign({
      propertyValuePaths: bindingConfig.propertyValuePaths
    }, fieldModifier && {
      modifier: fieldModifier
    })
  };
};

/** Map-level CHIRP→Render: converts a record of CHIRP `FieldConfig` into the REST wire shape, keyed by field path. */
export const fieldConfigsToRenderShape = configs => {
  const result = {};
  for (const [fieldPath, config] of Object.entries(configs)) {
    result[fieldPath] = fieldConfigToRenderShape(config);
  }
  return result;
};